import { Routes } from '@angular/router';
import { MSAL_REDIRECT_BRIDGE_PATH, ValRedirectBridgeComponent } from './common/components/val-redirect-bridge/val-redirect-bridge.component';
import { roles } from './services/roles';
import { vrcAccountResolver } from './services/vrc-account-resolver.service';
import { vrcAuthInitGuard } from './services/vrc-auth-init.guard';
import { vrcAuthGuard } from './services/vrc-auth.guard';
import { vrcLookupResolver } from './services/vrc-lookup-resolver.service';
import { vrcOfferResolver } from './services/vrc-offer-resolver.service';
import { vrcRoleGuard } from './services/vrc-role.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./components/vrc-login.component').then(m => m.VrcLoginComponent)
  },
  {
    path: 'nice',
    loadComponent: () => import('./components/vrc-nice-login.component').then(m => m.VrcNiceLoginComponent)
  },
  {
    path: 'payment',
    loadComponent: () => import('./components/vrc-payment.component').then(m => m.VrcPaymentComponent)
  },
  {
    path: MSAL_REDIRECT_BRIDGE_PATH,
    component: ValRedirectBridgeComponent
  },
  {
    path: 'additional',
    canActivate: [vrcAuthInitGuard, vrcAuthGuard, vrcRoleGuard],
    resolve: { _: vrcLookupResolver },
    loadComponent: () => import('./components/vrc-additional.component').then(m => m.VrcAdditionalComponent),
    data: { roles: [roles.Comm_List, roles.Offer_List, roles.Res_List, roles.Vouc_List] },
    children: [
      {
        path: '',
        redirectTo: 'vouchers',
        pathMatch: 'prefix'
      },
      {
        path: 'communications',
        loadComponent: () => import('./components/vrc-additional-main.component').then(m => m.VrcAdditionalMainComponent),
        children: [
          {
            path: '',
            redirectTo: 'search',
            pathMatch: 'prefix'
          },
          {
            path: 'list',
            loadComponent: () => import('./components/vrc-communications-list.component').then(m => m.VrcCommunicationsListComponent),
            data: { roles: [roles.Comm_List] }
          },
          {
            path: 'search',
            loadComponent: () => import('./components/vrc-communications-search.component').then(m => m.VrcCommunicationsSearchComponent),
            data: { roles: [roles.Comm_List] }
          },
        ]
      },
      {
        path: 'reservations',
        loadComponent: () => import('./components/vrc-additional-main.component').then(m => m.VrcAdditionalMainComponent),
        children: [
          {
            path: '',
            redirectTo: 'search',
            pathMatch: 'prefix'
          },
          {
            path: 'list',
            loadComponent: () => import('./components/vrc-reservations-list.component').then(m => m.VrcReservationsListComponent),
            data: { roles: [roles.Res_List] }
          },
          {
            path: 'search',
            loadComponent: () => import('./components/vrc-reservations-search.component').then(m => m.VrcReservationsSearchComponent),
            data: { roles: [roles.Res_List] }
          }
        ]
      },
      {
        path: 'offers',
        loadComponent: () => import('./components/vrc-additional-main.component').then(m => m.VrcAdditionalMainComponent),
        children: [
          {
            path: '',
            redirectTo: 'search',
            pathMatch: 'prefix'
          },
          {
            path: 'list',
            loadComponent: () => import('./components/vrc-offers-list.component').then(m => m.VrcOffersListComponent),
            data: { roles: [roles.Offer_List] }
          },
          {
            path: 'search',
            loadComponent: () => import('./components/vrc-offers-search.component').then(m => m.VrcOffersSearchComponent),
            data: { roles: [roles.Offer_List] }
          },
        ]
      },
      {
        path: 'vouchers',
        loadComponent: () => import('./components/vrc-additional-main.component').then(m => m.VrcAdditionalMainComponent),
        children: [
          {
            path: '',
            redirectTo: 'search',
            pathMatch: 'prefix'
          },
          {
            path: 'list',
            loadComponent: () => import('./components/vrc-vouchers-list.component').then(m => m.VrcVouchersListComponent),
            data: { roles: [roles.Vouc_List] }
          },
          {
            path: 'search',
            loadComponent: () => import('./components/vrc-vouchers-search.component').then(m => m.VrcVouchersSearchComponent),
            data: { roles: [roles.Vouc_List] }
          },
        ]
      },
    ]
  },
  {
    path: 'main',
    canActivate: [vrcAuthInitGuard, vrcAuthGuard, vrcRoleGuard],
    resolve: { _: vrcLookupResolver },
    loadComponent: () => import('./components/vrc-main.component').then(m => m.VrcMainComponent),
    data: { roles: [roles.Account_Read] },
    children: [
      {
        path: '',
        loadComponent: () => import('./components/vrc-empty.component').then(m => m.VrcEmptyComponent)
      },
      {
        path: 'account/:id',
        resolve: { account: vrcAccountResolver },
        loadComponent: () => import('./components/vrc-account.component').then(m => m.VrcAccountComponent),
        data: { roles: [roles.Account_Read] },
        children: [
          {
            path: '',
            redirectTo: 'summary',
            pathMatch: 'prefix'
          },
          {
            path: 'summary',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-summary.component').then(m => m.VrcSummaryComponent),
            data: { roles: [roles.Account_Read] },
          },
          {
            path: 'details',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-details.component').then(m => m.VrcDetailsComponent),
            data: { roles: [roles.Account_Read] },
          },
          {
            path: 'communication',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-communication.component').then(m => m.VrcCommunicationComponent),
            data: { roles: [roles.Comm_Read] },
          },
          {
            path: 'thread',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-communication-thread.component').then(m => m.VrcCommunicationThreadComponent),
            data: { roles: [roles.Comm_Read] },
          },
          {
            path: 'reservations',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-reservations.component').then(m => m.VrcReservationsComponent),
            data: { roles: [roles.Res_Read, roles.Offer_Read] },
          },
          {
            path: 'campaigns',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-campaings.component').then(m => m.VrcCampaignsComponent),
            data: { roles: [roles.Vouc_Read] },
          },
          {
            path: 'loyalty',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-loyality.component').then(m => m.VrcLoyalityComponent),
            data: { roles: [roles.Loy_Read] },
            children: [
              {
                path: ':id',
                loadComponent: () => import('./components/vrc-loyality-details.component').then(m => m.VrcLoyalityDetailsComponent),
              }
            ]
          },
          {
            path: 'valamartist',
            canActivate: [vrcRoleGuard],
            loadComponent: () => import('./components/vrc-valamartist.component').then(m => m.VrcValamartistComponent),
            data: { roles: [roles.Loy_Read] },
            children: [
              {
                path: ':id',
                loadComponent: () => import('./components/vrc-valamartist-details.component').then(m => m.VrcValamartistDetailsComponent),
              }
            ]
          }
        ]
      },
      {
        path: 'search/:type/:phrase',
        loadComponent: () => import('./components/vrc-search.component').then(m => m.VrcSearchComponent)
      },
      {
        path: 'offer/:id/:accountId',
        resolve: { offer: vrcOfferResolver },
        loadComponent: () => import('./components/vrc-offer.component').then(m => m.VrcOfferComponent)
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'main',
    pathMatch: 'prefix'
  }
];
